import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import i18n from 'i18n-js';
import Box from '@material-ui/core/Box';
import useStyles from './Wizard.styled';
import Link from '@material-ui/core/Link';
import Step from '@material-ui/core/Step';
import Card from '@material-ui/core/Card';
import { BaseModal } from 'components/UI';
import Button from '@material-ui/core/Button';
import Stepper from '@material-ui/core/Stepper';
import StepButton from '@material-ui/core/StepButton';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';

const Wizard = ({
  title = '',
  exit,
  steps = [],
  onNext = () => {},
  loading,
  children,
  position = 1,
  onPrevious = () => {},
  handleExit = () => {},
  onSaveDraft,
  onDiscard,
  showPrevious,
  lastStepLabel,
  saveButtonText,
  displayDiscardButton,
  showCancelButton,
  customStepperClass,
  customContentClass,
  displayDraftButton,
  handleEndOnboarding = () => {},
  showSaveAndExitModal,
  customStepperRootClass,
  handleShowOnSaveAndExitModal,
  customRootClass,
  nextStepLabel,
  displaCancelFormButton,
  onCancelForm,
  onCotinueForm,
  customNextBtnClass,
  customCancelFormBtnClass,
  displayContinueButton,
  cancelStepLabel,
}) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(position);
  const [openCancelModal, setOpenCancelModal] = useState(false);

  const handleNextStep = () => {
    setActiveStep(position);
    onNext();
  };

  const handlePrevStep = () => {
    onPrevious();
  };

  const handleStep = () => {
    onNext();
  };

  const handleOpenCancelModal = () => {
    setOpenCancelModal((prevState) => !prevState);
  };

  useEffect(() => {
    setActiveStep(position);
  }, [position]);

  const renderHeaderAction = () => {
    return (
      <>
        {showCancelButton ? (
          <Button
            color="primary"
            disabled={loading}
            data-cy="cancel-btn"
            className={classes.actionButtons}
            onClick={handleOpenCancelModal}
          >
            {i18n.t('wizard.cancel')}
          </Button>
        ) : (
          <div style={{ minHeight: '2rem' }} />
        )}
        {openCancelModal && (
          <BaseModal
            size="sm"
            open={openCancelModal}
            hasEnterButton
            hasCancelButton
            enableCloseButton={false}
            title={i18n.t('wizard.are_you_sure')}
            enterButtonLabel="Ok"
            // eslint-disable-next-line react/no-children-prop
            children={i18n.t('wizard.pending_changes')}
            onEnter={() => handleEndOnboarding(false, 'cancel')}
            onCancel={handleOpenCancelModal}
            handleClick={handleOpenCancelModal}
          />
        )}
        {showSaveAndExitModal && (
          <BaseModal
            size="sm"
            open={showSaveAndExitModal}
            hasEnterButton
            hasCancelButton
            enableCloseButton={false}
            title={i18n.t('wizard.save_and_exit')}
            enterButtonLabel={i18n.t('common.save')}
            // eslint-disable-next-line react/no-children-prop
            children={i18n.t('wizard.make_changes')}
            onEnter={handleEndOnboarding}
            onCancel={handleShowOnSaveAndExitModal}
            handleClick={handleShowOnSaveAndExitModal}
          />
        )}
      </>
    );
  };

  return (
    <Card className={clsx(classes.root, customRootClass)}>
      {title && (
        <CardHeader title={title} action={renderHeaderAction()} className={classes.headerRoot} />
      )}
      <Box height="100%" display="flex" flexDirection="column" flex="1">
        <Box className={clsx(classes.stepperContainer, customStepperClass)}>
          <Stepper
            nonLinear
            activeStep={activeStep}
            className={customStepperRootClass}
            data-cy="stepper-box"
          >
            {steps.map((step, index) => (
              <Step key={index}>
                <StepButton disabled={true} onClick={() => handleStep(index)}>
                  {step.label}
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Box>
        <Box
          flex="1"
          height="100%"
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
        >
          <CardContent className={clsx(classes.content, customContentClass)}>
            {children}
          </CardContent>

          <CardActions
            className={clsx(classes.cardActions, customContentClass, {
              [classes.flexEnd]: activeStep === 0 && !showPrevious,
            })}
          >
            {(activeStep !== 0 || showPrevious) && (
              <Link
                variant="body2"
                color="primary"
                disabled={loading}
                component="button"
                data-cy="previous-btn"
                onClick={(e) => {
                  e.preventDefault();
                  handlePrevStep();
                }}
                className={classes?.linkButton}
              >
                {i18n.t('wizard.back')}
              </Link>
            )}
            <Box>
              {displayDiscardButton ? (
                <Button
                  color="primary"
                  disabled={loading}
                  variant="outlined"
                  style={{
                    fontWeight: 500,
                  }}
                  data-cy="draft-btn"
                  onClick={onDiscard}
                  className={classes.actionButtons}
                >
                  {i18n.t('common.discard')}
                </Button>
              ) : null}
              {displayDraftButton && (
                <Button
                  color="primary"
                  disabled={loading}
                  variant="outlined"
                  style={{
                    fontWeight: 500,
                  }}
                  data-cy="draft-btn"
                  onClick={onSaveDraft}
                  className={classes.actionButtons}
                >
                  {i18n.t('wizard.save_and_exit')}
                </Button>
              )}
              {displaCancelFormButton && (
                <Button
                  color="primary"
                  variant="outlined"
                  style={{
                    fontWeight: 500,
                  }}
                  data-cy="cancel-form-btn"
                  onClick={onCancelForm}
                  className={clsx(classes.actionButtons, customCancelFormBtnClass)}
                >
                  {cancelStepLabel || i18n.t('wizard.cancel')}
                </Button>
              )}
              <Button
                color="primary"
                disabled={loading}
                variant={displayContinueButton ? 'outlined' : 'contained'}
                style={{
                  fontWeight: 500,
                }}
                data-cy="save-btn"
                onClick={() => {
                  if (exit) {
                    handleExit();
                  }
                  handleNextStep();
                }}
                className={clsx(classes.actionButtons, customNextBtnClass)}
              >
                {saveButtonText ||
                  (steps.length === position + 1
                    ? lastStepLabel || i18n.t('wizard.save_and_exit')
                    : nextStepLabel || i18n.t('wizard.next'))}
              </Button>
              {displayContinueButton && (
                <Button
                  color="primary"
                  variant="contained"
                  style={{
                    fontWeight: 500,
                  }}
                  data-cy="continue-form-btn"
                  onClick={onCotinueForm}
                  className={clsx(classes.actionButtons, customCancelFormBtnClass)}
                >
                  {i18n.t('common.continue')}
                </Button>
              )}
            </Box>
          </CardActions>
        </Box>
      </Box>
    </Card>
  );
};

Wizard.propTypes = {
  title: PropTypes.string.isRequired,
  exit: PropTypes.bool,
  loading: PropTypes.bool,
  position: PropTypes.number,
  onNext: PropTypes.func.isRequired,
  steps: PropTypes.array.isRequired,
  onPrevious: PropTypes.func.isRequired,
  handleEndOnboarding: PropTypes.func.isRequired,
  nextStepLabel: PropTypes.string,
  cancelStepLabel: PropTypes.string,
  displaCancelFormButton: PropTypes.bool,
  displayContinueButton: PropTypes.bool,
  onCancelForm: PropTypes.func,
  onCotinueForm: PropTypes.func,
  customNextBtnClass: PropTypes.string,
  customCancelFormBtnClass: PropTypes.string,
  children: PropTypes.node,
  customRootClass: PropTypes.string,
  customStepperRootClass: PropTypes.string,
  customContentClass: PropTypes.string,
  showPrevious: PropTypes.bool,
  showCancelButton: PropTypes.bool,
  showSaveAndExitModal: PropTypes.bool,
  handleShowOnSaveAndExitModal: PropTypes.func,
  handleExit: PropTypes.func,
  onSaveDraft: PropTypes.func,
  onDiscard: PropTypes.func,
  displayDraftButton: PropTypes.bool,
  displayDiscardButton: PropTypes.bool,
  lastStepLabel: PropTypes.string,
  saveButtonText: PropTypes.string,
  customStepperClass: PropTypes.string,
};

Wizard.defaultProps = {
  title: '',
  steps: [],
  position: 1,
  onNext: () => {},
  onPrevious: () => {},
  handleEndOnboarding: () => {},
};

export default Wizard;
